/**
 * Las cifras que importan de un vistazo.
 *
 * No se llama «pulso» en pantalla. El dueño lee números con color y peso,
 * no una tabla de operador.
 */
import { Link } from 'react-router'

import { cn } from '../lib/cn'
import type { PulsoPeriodo } from '../lib/tipos'

const TRAMOS: {
    clave: keyof PulsoPeriodo
    titulo: string
    ayuda: string
    destino: 'total' | 'cerradasBien' | 'cancelaciones' | 'enEspera'
    acento: string
    fondo: string
    anillo: string
}[] = [
    {
        clave: 'conversaciones',
        titulo: 'Te escribieron',
        ayuda: 'Personas que han hablado contigo',
        destino: 'total',
        acento: 'text-sky-300',
        fondo: 'from-sky-500/25 via-sky-500/5 to-neutral-950',
        anillo: 'border-sky-500/25 hover:border-sky-400/50',
    },
    {
        clave: 'citas',
        titulo: 'Citas hechas',
        ayuda: 'Reservas que el asistente cerró',
        destino: 'cerradasBien',
        acento: 'text-emerald-300',
        fondo: 'from-emerald-500/25 via-emerald-500/5 to-neutral-950',
        anillo: 'border-emerald-500/25 hover:border-emerald-400/50',
    },
    {
        clave: 'cancelaciones',
        titulo: 'Citas anuladas',
        ayuda: 'Las que se quitaron de la agenda',
        destino: 'cancelaciones',
        acento: 'text-amber-300',
        fondo: 'from-amber-500/20 via-amber-500/5 to-neutral-950',
        anillo: 'border-amber-500/25 hover:border-amber-400/50',
    },
    {
        clave: 'espera',
        titulo: 'En espera',
        ayuda: 'Se apuntaron a la lista',
        destino: 'enEspera',
        acento: 'text-orange-300',
        fondo: 'from-orange-500/20 via-orange-500/5 to-neutral-950',
        anillo: 'border-orange-500/25 hover:border-orange-400/50',
    },
]

export function rutaTramoPulso(
    destino: 'total' | 'cerradasBien' | 'cancelaciones' | 'enEspera',
    accountId: number | string,
    periodo: string,
    incluirTests = false,
): string {
    const tests = incluirTests ? '&incluirTests=1' : ''
    if (destino === 'cancelaciones') {
        return `/business/${accountId}/cancelaciones?periodo=${periodo}${tests}`
    }
    if (destino === 'enEspera') {
        return `/business/${accountId}/espera`
    }
    return `/business/${accountId}/counter/${destino}?periodo=${periodo}${tests}`
}

export function Pulso({
    datos,
    accountId,
    periodo,
    incluirTests = false,
}: {
    datos: PulsoPeriodo
    accountId: number
    periodo: string
    incluirTests?: boolean
}) {
    const tope = Math.max(datos.conversaciones, datos.citas, datos.cancelaciones, datos.espera, 1)

    return (
        <section aria-label="Pulso del periodo">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {TRAMOS.map((t) => {
                    const n = datos[t.clave]
                    const ancho = n === 0 ? 0 : Math.max(8, Math.round((n / tope) * 100))
                    return (
                        <Link
                            key={t.clave}
                            to={rutaTramoPulso(t.destino, accountId, periodo, incluirTests)}
                            className={cn(
                                'relative overflow-hidden rounded-2xl border bg-gradient-to-br p-5 shadow-lg shadow-black/20 transition',
                                t.fondo,
                                t.anillo,
                            )}
                        >
                            <div className={cn('text-5xl font-semibold tabular-nums tracking-tight', t.acento)}>
                                {n}
                            </div>
                            <div className="mt-2 text-sm font-medium text-neutral-100">{t.titulo}</div>
                            <div className="mt-0.5 text-sm leading-snug text-neutral-400">{t.ayuda}</div>
                            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-neutral-800/80">
                                <div
                                    className={cn('h-full rounded-full', t.acento.replace('text-', 'bg-'))}
                                    style={{ width: `${ancho}%` }}
                                />
                            </div>
                        </Link>
                    )
                })}
            </div>
        </section>
    )
}

export function portalSinActividad(pulso: PulsoPeriodo): boolean {
    return pulso.conversaciones === 0 && pulso.cancelaciones === 0
}
